import { useEffect, useState } from 'react';
import { useSafe } from '../context/SafeContext';
import { isBiometricUnlockSupported } from '../crypto/biometricUnlock';
import { HbsButton } from './hbs/Button';
import { HbsInput } from './hbs/Input';
import { Icon } from './hbs/Icon';
import { EyeToggle } from './hbs/EyeToggle';

/** Cosmetic platform label for the biometric. */
function biometricLabel(): string {
  const p = typeof navigator !== 'undefined' ? `${navigator.platform} ${navigator.userAgent}` : '';
  if (/Win/i.test(p)) return 'Windows Hello';
  if (/Mac|iPhone|iPad/i.test(p)) return 'Touch ID';
  return 'biometric unlock';
}

/**
 * Settings section for biometric unlock (WebAuthn PRF). Renders nothing on
 * platforms without a usable platform authenticator, so the Settings page shows
 * no empty section. Enabling collects the safe password once — the context
 * verifies it, then binds it behind the biometric. See
 * web/src/crypto/biometricUnlock.ts.
 */
export function BiometricUnlockSection(): JSX.Element | null {
  const { biometricEnrolled, enableBiometricUnlock, disableBiometricUnlock } = useSafe();

  const [supported, setSupported] = useState<boolean | null>(null);
  const [entering, setEntering] = useState(false);
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const label = biometricLabel();

  useEffect(() => {
    let cancelled = false;
    void isBiometricUnlockSupported().then((ok) => { if (!cancelled) setSupported(ok); });
    return () => { cancelled = true; };
  }, []);

  if (supported !== true) return null;

  async function handleEnable(): Promise<void> {
    setError('');
    setBusy(true);
    try {
      const result = await enableBiometricUnlock(password);
      setPassword('');
      if (result.ok) {
        setEntering(false);
        return;
      }
      if (result.code === 'INVALID_PASSWORD') setError('Incorrect safe password.');
      else if (result.code === 'USER_CANCELED') setError(`${label} was cancelled.`);
      else if (result.code === 'NO_PRF') setError('Your device or browser doesn’t support biometric unlock.');
      else setError(`Couldn’t enable ${label}.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
      <h2 className="font-display text-[18px] text-hbs-text mb-4">Biometric unlock</h2>

      {biometricEnrolled ? (
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[15px] text-hbs-text flex items-center gap-2">
              <Icon name="fingerprint" size={16} className="text-hbs-accent" />
              {label} is on
            </p>
            <p className="text-[13px] text-hbs-text-mute mt-1">
              Unlock your safe with {label} instead of typing your safe password.
            </p>
          </div>
          <HbsButton variant="subtle" id="biometric-turn-off-button" onClick={disableBiometricUnlock}>
            Turn off
          </HbsButton>
        </div>
      ) : entering ? (
        <div className="flex flex-col gap-3 max-w-sm">
          <p className="text-[13px] text-hbs-text-mute">
            Enter your safe password once to bind it behind {label}. It’s stored
            encrypted on this device and never leaves it.
          </p>
          <HbsInput
            label="Safe password"
            type={revealed ? 'text' : 'password'}
            value={password}
            onChange={setPassword}
            autoFocus
            mono={revealed}
            leadingIcon={<Icon name="key" size={16} className="text-hbs-accent" />}
            trailingActions={<EyeToggle revealed={revealed} onChange={setRevealed} />}
          />
          {error && <p className="text-[13px] text-hbs-danger" role="alert">{error}</p>}
          <div className="flex gap-2">
            <HbsButton id="biometric-confirm-button" onClick={handleEnable} loading={busy} disabled={!password}>
              Confirm
            </HbsButton>
            <HbsButton
              variant="plain"
              id="biometric-cancel-button" onClick={() => { setEntering(false); setPassword(''); setError(''); }}
            >
              Cancel
            </HbsButton>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[13px] text-hbs-text-mute">
              Unlock your safe with {label} instead of typing your safe password.
              Your password is stored encrypted on this device, never on our servers.
            </p>
          </div>
          <HbsButton variant="subtle" id="biometric-turn-on-button" onClick={() => { setError(''); setEntering(true); }}>
            Turn on
          </HbsButton>
        </div>
      )}
    </section>
  );
}
